/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  DynamicFormsService,
  IDynamicForm,
} from '@kinet-open-network/client/core/forms/util';
import { toast } from '@kinet-open-network/client/shared/ui';
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from '@ngx-formly/core';
import { ButtonModule } from 'primeng/button';

export interface DynamicFormOptions {
  submitText?: string;
  buttonClass?: string;
  buttonIcon?: string;
  buttonHidden?: boolean;
  buttonDisabled?: boolean;
  formState?: any;
}

export interface DynamicFormField extends FormlyFieldConfig {}

@Component({
  selector: 'kon-dynamic-form',
  standalone: true,
  imports: [CommonModule, FormlyModule, ReactiveFormsModule, ButtonModule],
  template: `
    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit($event)"
      [ngClass]="{ 'show-dirty-styles': showDirtyStyles }"
    >
      <formly-form
        [form]="form"
        [fields]="fields"
        [options]="formlyOptions"
        [model]="model"
        (modelChange)="modelChange.emit($event)"
      ></formly-form>

      @if(!options.buttonHidden) {
      <div class="w-full sticky bottom-0 pb-4 bg-white grid z-30">
        <button
          pButton
          type="submit"
          [class]="options.buttonClass"
          [loading]="loading"
          [label]="options.submitText || 'Uložiť'"
          [disabled]="buttonDisabled || (!validateOnSubmit && !form.valid)"
        >
          <i [class]="options.buttonIcon"> </i>
        </button>
      </div>
      }
    </form>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormComponent implements OnInit, OnDestroy, IDynamicForm {
  private formsService = inject(DynamicFormsService);
  private route = inject(ActivatedRoute);

  form = new FormGroup({});
  private _model: any = {};

  @Input() fields: DynamicFormField[] = [];
  @Input() loading = false;
  @Input() preventCloseIfDirty = true;
  @Input() showDirtyStyles = false;
  // when true, submit button stays enabled on invalid forms; onSubmit() validates and toasts instead
  @Input() validateOnSubmit = false;

  formlyOptions: FormlyFormOptions = {};

  private _options: DynamicFormOptions = {
    submitText: 'Uložiť',
    buttonClass: 'w-full',
  };

  @Input() set options(value: DynamicFormOptions) {
    if (value.formState) {
      this.formlyOptions.formState = value.formState;
    }

    this._options = { ...value };
  }
  get options(): DynamicFormOptions {
    return this._options;
  }

  get buttonDisabled() {
    if (this.options.buttonDisabled) {
      return true;
    }

    if (this.formlyOptions?.formState?.disabled !== undefined) {
      if (typeof this.options.formState.disabled === 'function') {
        return this.options.formState.disabled();
      }

      return !!this.options.formState.disabled;
    }

    return false;
  }

  @Input() set model(value: any) {
    this._model = { ...value };
  }
  get model(): any {
    return this._model;
  }

  reset(model?: any) {
    this.formlyOptions.resetModel?.(model);
  }

  updateInitialModel(model?: any) {
    this.formlyOptions.updateInitialValue?.(model);
  }

  @Output() modelChange = new EventEmitter<any>();

  @Output() submitted = new EventEmitter<any>();

  ngOnInit(): void {
    this.formsService.addForm(this);
    this.form.valueChanges.subscribe(() => {
      if (this.preventCloseIfDirty) {
        this.preserveDirtyState();
      }
    });
  }

  ngOnDestroy(): void {
    this.formsService.removeForm(this);
    this.purgeDirtyState();
  }

  onSubmit(event?: Event): void {
    if (this.validateOnSubmit && this.form.invalid) {
      event?.stopPropagation();
      this.form.markAllAsTouched();
      const invalidPaths = this.collectInvalidFieldPaths(this.fields);
      toast({
        severity: 'danger',
        summary: 'Formulár obsahuje chyby',
        detail: invalidPaths.length ? invalidPaths.join(', ') : undefined,
        life: 10000,
      });
      return;
    }

    this.purgeDirtyState();
    this.submitted.emit({ ...this.model });
  }

  // walks the formly field tree (tabs/fieldGroup/fieldArray rows) to build "Parent -> Field" paths of invalid leaf fields
  private collectInvalidFieldPaths(
    fields: DynamicFormField[],
    path: string[] = [],
  ): string[] {
    const paths: string[] = [];

    fields.forEach((field, index) => {
      const isArrayItem = !!field.parent?.fieldArray;
      const label = (field.props?.label ?? field.props?.['collapseTitle'])
        ?.toString()
        .replace(/\s*\*\s*$/, '');

      if (field.fieldGroup?.length) {
        let childPath = path;
        if (isArrayItem) {
          childPath = path.length
            ? [...path.slice(0, -1), `${path[path.length - 1]} [${index}]`]
            : [`[${index}]`];
        } else if (label) {
          childPath = [...path, label];
        }

        paths.push(
          ...this.collectInvalidFieldPaths(field.fieldGroup, childPath),
        );
        return;
      }

      if (field.formControl?.invalid && label) {
        paths.push([...path, label].join(' -> '));
      }
    });

    return Array.from(new Set(paths));
  }

  setValue(value: any): void {
    this.model = { ...value };
  }

  @HostListener('window:beforeunload')
  canDeactivate() {
    const isFormDirty = (window as any)?.formsState?.[
      this.route.snapshot.url.toString()
    ];
    if (isFormDirty) {
      return false;
    }
    return true;
  }

  preserveDirtyState() {
    (window as any).formsState = {
      ...(window as any).formsState,
      [this.route.snapshot.url.toString()]: this.form.dirty,
    };
  }

  purgeDirtyState() {
    delete (window as any)?.formsState?.[this.route.snapshot.url.toString()];
    this.form.markAsPristine();
  }
}
